import { useState, useEffect, useMemo } from 'react';
import {
    Search, ChevronDown, ArrowUp, Check, Download, Loader2, Edit, ChevronLeft, ChevronRight, Trash2, X
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import api from '../../lib/api';
import { exportQuotesToExcel } from '../../utils/exportQuotes';
import { canAccess } from '../../utils/permissions';
import { FileText } from 'lucide-react';
import UpdateQuoteModal from './components/UpdateQuoteModal';

const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const getStatusBadge = (status) => {
    const statusMap = {
        'pending': { label: 'Pending', className: 'bg-yellow-100 text-yellow-700' },
        'accepted': { label: 'Accepted', className: 'bg-emerald-100 text-emerald-700' },
        'rejected': { label: 'Rejected', className: 'bg-red-100 text-red-700' },
    };
    return statusMap[status] || { label: status || 'Unknown', className: 'bg-gray-100 text-gray-700' };
};

export default function Quotes() {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [showStatusMenu, setShowStatusMenu] = useState(false);
    const [showServiceMenu, setShowServiceMenu] = useState(false);
    const [selectedStatus, setSelectedStatus] = useState('All');
    const [selectedService, setSelectedService] = useState('All');
    const [searchTerm, setSearchTerm] = useState('');
    const [isExporting, setIsExporting] = useState(false);
    const [selectedQuote, setSelectedQuote] = useState(null);
    const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const perPage = 15;

    // Delete Modal State
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [idToDelete, setIdToDelete] = useState(null);

    // Fetch quotes from API with pagination
    const { data: quotesResponse, isLoading, error, refetch } = useQuery({
        queryKey: ['companyQuotes', currentPage],
        queryFn: async () => {
            const response = await api.get('/company/quotes2', {
                params: { page: currentPage }
            });
            return response.data;
        },
        queryFn: async () => {
            const response = await api.get('/company/quotes2', {
                params: { page: currentPage }
            });
            return response.data;
        },
        keepPreviousData: true,
    });

    const deleteQuoteMutation = useMutation({
        mutationFn: async (id) => {
            return await api.delete(`/company/quotes/${id}/`);
        },
        onSuccess: () => {
            toast.success('Quote deleted successfully');
            queryClient.invalidateQueries(['companyQuotes']);
            setShowDeleteModal(false);
            setIdToDelete(null);
        },
        onError: (error) => {
            toast.error(error.response?.data?.message || 'Failed to delete quote');
            setShowDeleteModal(false);
        },
    });

    const handleDelete = (e, id) => {
        e.stopPropagation();
        setIdToDelete(id);
        setShowDeleteModal(true);
    };

    const confirmDelete = () => {
        if (idToDelete) {
            deleteQuoteMutation.mutate(idToDelete);
        }
    };

    const quotes = quotesResponse?.data || [];
    const pagination = quotesResponse?.pagination || {
        current_page: 1,
        per_page: perPage,
        total: 0,
        last_page: 1,
        from: 0,
        to: 0
    };

    // Close dropdowns on outside click
    useEffect(() => {
        const handleClickOutside = () => {
            setShowStatusMenu(false);
            setShowServiceMenu(false);
        };
        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    }, []);

    // Build dynamic status filter options
    const statusOptions = useMemo(() => {
        const statusCounts = {};
        quotes.forEach(quote => {
            const status = quote.status || 'unknown';
            statusCounts[status] = (statusCounts[status] || 0) + 1;
        });

        const options = [{ label: 'All', value: 'All', count: quotes.length }];
        Object.keys(statusCounts).forEach(status => {
            const capitalized = status.charAt(0).toUpperCase() + status.slice(1);
            options.push({
                label: capitalized,
                value: status,
                count: statusCounts[status]
            });
        });
        return options;
    }, [quotes]);

    // Build dynamic service filter options
    const serviceOptions = useMemo(() => {
        const serviceCounts = {};
        quotes.forEach(quote => {
            const serviceName = quote.service?.name || 'Unknown';
            serviceCounts[serviceName] = (serviceCounts[serviceName] || 0) + 1;
        });

        const options = [{ label: 'All', value: 'All', count: quotes.length }];
        Object.keys(serviceCounts).forEach(serviceName => {
            options.push({
                label: serviceName,
                value: serviceName,
                count: serviceCounts[serviceName]
            });
        });
        return options;
    }, [quotes]);

    // Filter quotes based on status, service, and search
    const filteredQuotes = useMemo(() => {
        let filtered = [...quotes];

        // Apply status filter
        if (selectedStatus !== 'All') {
            filtered = filtered.filter(quote => quote.status === selectedStatus);
        }

        // Apply service filter
        if (selectedService !== 'All') {
            filtered = filtered.filter(quote => quote.service?.name === selectedService);
        }

        // Apply search filter
        if (searchTerm.trim()) {
            const searchLower = searchTerm.toLowerCase();
            filtered = filtered.filter(quote => {
                const firstName = (quote.first_name || '').toLowerCase();
                const lastName = (quote.last_name || '').toLowerCase();
                const email = (quote.email || '').toLowerCase();
                const boatType = (quote.boat_type || '').toLowerCase();
                const boatMake = (quote.boat_make || '').toLowerCase();
                const boatLocation = (quote.boat_location || '').toLowerCase();
                const serviceName = (quote.service?.name || '').toLowerCase();

                return firstName.includes(searchLower) ||
                    lastName.includes(searchLower) ||
                    email.includes(searchLower) ||
                    boatType.includes(searchLower) ||
                    boatMake.includes(searchLower) ||
                    boatLocation.includes(searchLower) ||
                    serviceName.includes(searchLower);
            });
        }

        return filtered;
    }, [quotes, selectedStatus, selectedService, searchTerm]);

    // Calculate stats (based on current page data)
    const stats = useMemo(() => {
        const total = pagination.total || quotes.length;
        const accepted = quotes.filter(q => q.status === 'accepted').length;
        const pending = quotes.filter(q => q.status === 'pending').length;
        const rejected = quotes.filter(q => q.status === 'rejected').length;
        const conversionRate = total > 0 ? Math.round((accepted / total) * 100) : 0;

        return { total, accepted, pending, rejected, conversionRate };
    }, [quotes, pagination.total]);

    // Reset to page 1 when filters change
    useEffect(() => {
        setCurrentPage(1);
    }, [selectedStatus, selectedService, searchTerm]);

    if (!canAccess("view_quote")) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[60vh] p-8 text-center text-sm">
                <div className="w-20 h-20 bg-red-50 rounded-full flex items-center justify-center mb-6">
                    <FileText className="w-10 h-10 text-red-600" />
                </div>
                <h2 className="text-3xl font-black text-gray-900 mb-2 tracking-tight">Access Restricted</h2>
                <p className="text-gray-500 max-w-md font-medium">
                    You don't have the required permissions to view inquiries/quotes.
                </p>
            </div>
        );
    }

    const handleRowClick = (id) => {
        navigate(`/profile/details/quotes/${id}`);
    };

    const handleUpdateClick = (e, quote) => {
        e.stopPropagation();
        setSelectedQuote(quote);
        setIsUpdateModalOpen(true);
    };

    const handleUpdateSuccess = () => {
        queryClient.invalidateQueries(['companyQuotes']);
    };

    const handleCloseModal = () => {
        setIsUpdateModalOpen(false);
        setSelectedQuote(null);
    };

    const handleExport = () => {
        if (!filteredQuotes || filteredQuotes.length === 0) {
            return;
        }

        setIsExporting(true);

        // Small delay to show loading state
        setTimeout(() => {
            try {
                exportQuotesToExcel(filteredQuotes);
            } catch (error) {
                console.error('Export failed:', error);
            } finally {
                setIsExporting(false);
            }
        }, 100);
    };

    return (
        <section className="min-h-screen text-sm">
            {/* Header */}
            <div className="bg-white border-b border-gray-200 px-4 py-3">
                <div className="flex items-center justify-between mb-3">
                    <h1 className="text-xl font-bold text-gray-900">Quotes</h1>

                    <div className="flex items-center gap-2">
                        <button
                            onClick={handleExport}
                            disabled={isExporting || filteredQuotes.length === 0}
                            className="px-3 py-2 border border-gray-300 rounded-lg text-xs font-medium flex items-center gap-1.5 bg-white shadow-sm hover:bg-gray-50 disabled:opacity-70 transition"
                        >
                            {isExporting ? (
                                <>
                                    <Loader2 className="w-4 h-4 animate-spin text-gray-600" />
                                    Exporting...
                                </>
                            ) : (
                                <>
                                    <Download className="w-4 h-4 text-gray-600" />
                                    Export to Excel
                                </>
                            )}
                        </button>
                    </div>
                </div>

                {/* Overview Cards */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 hover:shadow transition">
                        <p className="text-xs font-medium text-gray-600">Overview</p>
                        <ul className="mt-2 space-y-1 text-xs">
                            {stats.pending > 0 && (
                                <li className="flex items-center gap-2">
                                    <span className="w-2.5 h-2.5 rounded-full bg-yellow-500"></span>
                                    <span className="font-medium">Pending ({stats.pending})</span>
                                </li>
                            )}
                            {stats.accepted > 0 && (
                                <li className="flex items-center gap-2">
                                    <span className="w-2.5 h-2.5 rounded-full bg-green-600"></span>
                                    <span className="font-medium">Accepted ({stats.accepted})</span>
                                </li>
                            )}
                            {stats.rejected > 0 && (
                                <li className="flex items-center gap-2">
                                    <span className="w-2.5 h-2.5 rounded-full bg-red-500"></span>
                                    <span className="font-medium">Rejected ({stats.rejected})</span>
                                </li>
                            )}
                            {stats.pending === 0 && stats.accepted === 0 && stats.rejected === 0 && (
                                <li className="text-gray-500">No quotes yet</li>
                            )}
                        </ul>
                    </div>

                    <div className="lg:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 hover:shadow transition">
                            <p className="text-xs font-medium text-gray-600">Sent</p>
                            <p className="text-xs text-gray-500">Total quotes</p>
                            <div className="flex items-end justify-between mt-2">
                                <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
                            </div>
                        </div>

                        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 hover:shadow transition">
                            <p className="text-xs font-medium text-gray-600">Converted</p>
                            <p className="text-xs text-gray-500">Accepted quotes</p>
                            <div className="flex items-end justify-between mt-2">
                                <p className="text-2xl font-bold text-gray-900">{stats.accepted}</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Conversion Rate */}
                <div className="mt-3 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-4 border border-blue-200">
                    <p className="text-xs font-medium text-blue-900">Conversion rate</p>
                    <p className="text-xs text-blue-700">Accepted / Total</p>
                    <div className="flex items-end justify-between mt-1">
                        <p className="text-2xl font-bold text-blue-900">{stats.conversionRate}%</p>
                        {stats.conversionRate > 0 && (
                            <div className="flex items-center gap-0.5 text-blue-600 text-xs font-medium">
                                <ArrowUp className="w-3.5 h-3.5" /> {stats.conversionRate}%
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Table Section */}
            <div className="px-4 py-5">
                <div className="flex items-center justify-between mb-3">
                    <div>
                        <h2 className="text-lg font-bold text-gray-900">All quotes</h2>
                        <p className="text-xs text-gray-600">({filteredQuotes.length} result{filteredQuotes.length !== 1 ? 's' : ''})</p>
                    </div>

                    <div className="flex items-center gap-2">
                        <div className="relative">
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setShowStatusMenu(!showStatusMenu);
                                }}
                                className="px-3 py-2 bg-white border border-gray-300 rounded-lg text-xs font-medium flex items-center gap-1.5 shadow-sm"
                            >
                                Status
                                <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showStatusMenu ? 'rotate-180' : ''}`} />
                            </button>

                            {showStatusMenu && (
                                <>
                                    <div className="fixed inset-0 z-10" onClick={() => setShowStatusMenu(false)} />
                                    <div className="absolute top-full right-0 mt-1.5 w-56 bg-white rounded-xl shadow-xl border border-gray-200 z-20 text-xs">
                                        {statusOptions.map((status) => (
                                            <button
                                                key={status.value}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setSelectedStatus(status.value);
                                                    setShowStatusMenu(false);
                                                }}
                                                className="w-full text-left px-3 py-2.5 hover:bg-gray-50 flex items-center justify-between"
                                            >
                                                <span>{status.label}</span>
                                                <span className="text-gray-500">({status.count})</span>
                                                {selectedStatus === status.value && <Check className="ml-1 w-4 h-4 text-emerald-600" />}
                                            </button>
                                        ))}
                                    </div>
                                </>
                            )}
                        </div>

                        <div className="relative">
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setShowServiceMenu(!showServiceMenu);
                                }}
                                className="px-3 py-2 bg-white border border-gray-300 rounded-lg text-xs font-medium flex items-center gap-1.5 shadow-sm"
                            >
                                Service
                                <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showServiceMenu ? 'rotate-180' : ''}`} />
                            </button>

                            {showServiceMenu && (
                                <>
                                    <div className="fixed inset-0 z-10" onClick={() => setShowServiceMenu(false)} />
                                    <div className="absolute top-full right-0 mt-1.5 w-48 bg-white rounded-xl shadow-xl border border-gray-200 z-20 text-xs">
                                        {serviceOptions.map((service) => (
                                            <button
                                                key={service.value}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setSelectedService(service.value);
                                                    setShowServiceMenu(false);
                                                }}
                                                className="w-full text-left px-3 py-2.5 hover:bg-gray-50 flex items-center justify-between"
                                            >
                                                <span>{service.label}</span>
                                                <span className="text-gray-500">({service.count})</span>
                                                {selectedService === service.value && <Check className="ml-1 w-4 h-4 text-emerald-600" />}
                                            </button>
                                        ))}
                                    </div>
                                </>
                            )}
                        </div>

                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Search quotes..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="pl-10 pr-3 py-2 w-64 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 text-xs bg-white"
                            />
                        </div>
                    </div>
                </div>

                {/* Loading State */}
                {isLoading && (
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center">
                        <p className="text-gray-600">Loading quotes...</p>
                    </div>
                )}

                {/* Error State */}
                {error && (
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center">
                        <p className="text-red-600 mb-4">Failed to load quotes</p>
                        <button
                            onClick={() => refetch()}
                            className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition text-xs"
                        >
                            Retry
                        </button>
                    </div>
                )}

                {/* Table */}
                {!isLoading && !error && (
                    <>
                        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden text-xs">
                            {filteredQuotes.length === 0 ? (
                                <div className="p-8 text-center text-gray-500">
                                    No quotes found
                                </div>
                            ) : (
                                <table className="w-full">
                                    <thead className="bg-gray-50 border-b border-gray-200 text-xs font-medium text-gray-700">
                                        <tr>
                                            <th className="text-left px-3 py-3">Client</th>
                                            <th className="text-left px-3 py-3">Quote #</th>
                                            <th className="text-left px-3 py-3">Service Type</th>
                                            <th className="text-left px-3 py-3">Lead Source</th>
                                            <th className="text-left px-3 py-3">Created</th>
                                            <th className="text-left px-3 py-3">Status</th>
                                            <th className="text-left px-3 py-3">Boat</th>
                                            <th className="text-left px-3 py-3">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className="text-xs">
                                        {filteredQuotes.map((quote) => {
                                            const clientName = `${quote.first_name || ''} ${quote.last_name || ''}`.trim() || quote.email || 'Unknown';
                                            const statusBadge = getStatusBadge(quote.status);
                                            const boatInfo = [quote.boat_type, quote.boat_make, quote.boat_year]
                                                .filter(Boolean)
                                                .join(' • ') || '-';

                                            return (
                                                <tr
                                                    key={quote.id}
                                                    className="border-b border-gray-100 hover:bg-gray-50 transition cursor-pointer"
                                                    onClick={() => handleRowClick(quote.id)}
                                                >
                                                    <td className="px-3 py-3">
                                                        <div className="font-medium text-gray-900">{clientName}</div>
                                                        <div className="text-xs text-gray-500">{quote.email || ''}</div>
                                                    </td>
                                                    <td className="px-3 py-3 font-medium text-gray-700">#{quote.id}</td>
                                                    <td className="px-3 py-3">
                                                        <span className="text-xs text-gray-700">{quote.service?.name || '-'}</span>
                                                    </td>
                                                    <td className="px-3 py-3">
                                                        <span className="text-xs text-gray-700">{quote.lead_source?.name || '-'}</span>
                                                    </td>
                                                    <td className="px-3 py-3 text-gray-600 text-xs">{formatDate(quote.created_at)}</td>
                                                    <td className="px-3 py-3">
                                                        <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${statusBadge.className}`}>
                                                            {statusBadge.label}
                                                        </span>
                                                    </td>
                                                    <td className="px-3 py-3 font-medium text-gray-900 text-xs">{boatInfo}</td>
                                                    <td className="px-3 py-3 flex gap-2">
                                                        <button
                                                            onClick={(e) => handleUpdateClick(e, quote)}
                                                            className="px-2 py-1.5 bg-emerald-50 text-emerald-700 rounded-lg text-xs font-medium hover:bg-emerald-100 transition flex items-center gap-1.5"
                                                            title="Update Quote"
                                                        >
                                                            <Edit className="w-3.5 h-3.5" />
                                                            
                                                        </button>
                                                        <button
                                                            onClick={(e) => handleDelete(e, quote.id)}
                                                            className="px-2 py-1.5 bg-red-50 text-red-700 rounded-lg text-xs font-medium hover:bg-red-100 transition flex items-center gap-1.5"
                                                            title="Delete Quote"
                                                        >
                                                            <Trash2 className="w-3.5 h-3.5" />
                                                            
                                                        </button>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            )}
                        </div>

                        {/* Pagination Controls */}
                        {pagination.last_page > 1 && (
                            <div className="mt-6 flex items-center justify-center gap-2">
                                <button
                                    onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                                    disabled={currentPage === 1 || isLoading}
                                    className="px-3 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition text-xs font-medium flex items-center gap-1.5"
                                >
                                    <ChevronLeft className="w-4 h-4" />
                                    Previous
                                </button>

                                {Array.from({ length: pagination.last_page }, (_, i) => i + 1).map((page) => (
                                    <button
                                        key={page}
                                        onClick={() => setCurrentPage(page)}
                                        disabled={isLoading}
                                        className={`px-3 py-2 rounded-lg transition text-xs font-medium disabled:opacity-50 disabled:cursor-not-allowed ${page === currentPage
                                            ? 'bg-emerald-600 text-white'
                                            : 'bg-white border border-gray-300 hover:bg-gray-50'
                                            }`}
                                    >
                                        {page}
                                    </button>
                                ))}

                                <button
                                    onClick={() => setCurrentPage((prev) => Math.min(prev + 1, pagination.last_page))}
                                    disabled={currentPage === pagination.last_page || isLoading}
                                    className="px-3 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition text-xs font-medium flex items-center gap-1.5"
                                >
                                    Next
                                    <ChevronRight className="w-4 h-4" />
                                </button>
                            </div>
                        )}

                        {/* Pagination Info */}
                        {pagination.total > 0 && (
                            <div className="mt-4 text-center text-xs text-gray-600">
                                Showing {pagination.from} to {pagination.to} of {pagination.total} quotes
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* Update Quote Modal */}
            <UpdateQuoteModal
                quote={selectedQuote}
                isOpen={isUpdateModalOpen}
                onClose={handleCloseModal}
                onSuccess={handleUpdateSuccess}
            />

            {/* Delete Confirmation Modal */}
            {showDeleteModal && (
                <>
                    <div className="fixed inset-0 bg-black/50 z-[60]" onClick={() => setShowDeleteModal(false)} />
                    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
                        <div className="bg-white rounded-xl shadow-2xl max-w-sm w-full p-6 animate-in fade-in zoom-in-95 duration-200">
                            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                <Trash2 className="w-6 h-6 text-red-600" />
                            </div>
                            <h3 className="text-lg font-bold text-gray-900 text-center mb-2">Delete Quote</h3>
                            <p className="text-sm text-gray-500 text-center mb-6">
                                Are you sure you want to delete this quote? This action cannot be undone.
                            </p>
                            <div className="flex gap-3">
                                <button
                                    onClick={() => setShowDeleteModal(false)}
                                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={confirmDelete}
                                    disabled={deleteQuoteMutation.isPending}
                                    className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 transition flex items-center justify-center gap-2"
                                >
                                    {deleteQuoteMutation.isPending ? (
                                        <>
                                            <Loader2 className="w-4 h-4 animate-spin" />
                                            Deleting...
                                        </>
                                    ) : (
                                        'Delete'
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </section>
    );
}